import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Autocomplete,
  Switch,
  Breadcrumbs,
  Link,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Description as FileIcon,
  Folder as FolderIcon,
  Visibility as PreviewIcon,
  ArrowBack as ArrowBackIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import solutionService from '../../services/solution.service';
import solutionContainerService from '../../services/solutionContainerService';
import patternService from '../../services/pattern.service';
import skillService from '../../services/skill.service';
import { Solution, ExercisePattern, SolutionContainer, Skill } from '../../types';
import { toast } from 'react-toastify';
import PatternSelectionDialog from '../../components/PatternSelectionDialog';
import PdfViewer from '../../components/PdfViewer';
import PatternPdfViewer from '../../components/PatternPdfViewer';
import PythonEditor from '../../components/PythonEditor';
import LatexEditor from '../../components/LatexEditor';
import { registerBeforeTabChange, unregisterBeforeTabChange } from './Workshop';

// Sortable Item Component for both solutions and containers
interface SortableItemProps {
  item: Solution | SolutionContainer;
  isContainer?: boolean;
  isSelected: boolean;
  onSelect: (item: Solution | SolutionContainer, isContainer: boolean) => void;
  onTogglePublic?: (solution: Solution, isPublic: boolean) => void;
  activeItemType?: string;
}

const SortableItem: React.FC<SortableItemProps> = ({
  item,
  isContainer = false,
  isSelected,
  onSelect,
  onTogglePublic,
  activeItemType,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: `${isContainer ? 'container' : 'solution'}-${item.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isOver && activeItemType === 'solution' && isContainer ? 'rgba(33, 150, 243, 0.2)' : undefined,
  };

  if (isContainer) {
    const container = item as SolutionContainer;
    return (
      <Card
        ref={setNodeRef}
        style={style}
        sx={{
          cursor: 'pointer',
          backgroundColor: '#f5f5f5',
          position: 'relative',
        }}
        onClick={() => onSelect(container, true)}
      >
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box {...attributes} {...listeners} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
              <DragIcon sx={{ color: 'action.disabled', fontSize: 18 }} />
            </Box>
            <FolderIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Box flex={1}>
              <Typography variant="subtitle2" fontWeight="bold">
                {container.title}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {container.solutions_count || 0} solution(s)
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  } else {
    const solution = item as Solution;
    return (
      <Card
        ref={setNodeRef}
        style={style}
        sx={{
          cursor: 'pointer',
          backgroundColor: isSelected ? 'action.selected' : solution.is_public ? 'rgba(33, 150, 243, 0.08)' : 'inherit',
          position: 'relative',
        }}
        onClick={() => onSelect(solution, false)}
      >
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box {...attributes} {...listeners} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
              <DragIcon sx={{ color: 'action.disabled', fontSize: 18 }} />
            </Box>
            <FileIcon sx={{ color: 'action.disabled', fontSize: 20 }} />
            <Box flex={1}>
              <Typography variant="subtitle2">
                {solution.pattern_title || `Solution ${solution.id}`}
              </Typography>
              {solution.tags && solution.tags.length > 0 && (
                <Typography variant="caption" color="textSecondary">
                  {solution.tags.slice(0, 2).join(', ')}
                  {solution.tags.length > 2 && '...'}
                </Typography>
              )}
            </Box>
            <Switch
              size="small"
              checked={solution.is_public || false}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                onTogglePublic?.(solution, e.target.checked);
              }}
              sx={{ ml: 0.5 }}
            />
          </Box>
        </CardContent>
      </Card>
    );
  }
};

interface SolutionsProps {
  urlContainerId?: number | null;
  onNavChange?: (containerId: number | null, itemId: number | null, label?: string) => void;
}

const Solutions: React.FC<SolutionsProps> = ({ urlContainerId, onNavChange }) => {
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [containers, setContainers] = useState<SolutionContainer[]>([]);
  const [currentContainer, setCurrentContainer] = useState<SolutionContainer | null>(null);
  const [navigationPath, setNavigationPath] = useState<SolutionContainer[]>([]);
  const [selectedSolution, setSelectedSolution] = useState<Solution | null>(null);
  const [latexTemplate, setLatexTemplate] = useState('');
  const [pythonFunction, setPythonFunction] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createDialogTab, setCreateDialogTab] = useState(1); // 0 = Container, 1 = Solution (default)
  const [selectedPattern, setSelectedPattern] = useState<ExercisePattern | null>(null);
  const [newContainerName, setNewContainerName] = useState('');
  const [patternDialogOpen, setPatternDialogOpen] = useState(false);
  const [patternModalOpen, setPatternModalOpen] = useState(false);
  const [patternDetails, setPatternDetails] = useState<ExercisePattern | null>(null);
  const [generationResults, setGenerationResults] = useState<any[] | null>(null);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);
  const [isPatternReusable, setIsPatternReusable] = useState(true);
  const [solutionPdfKey, setSolutionPdfKey] = useState(0);
  const [activeItemType, setActiveItemType] = useState<string>('');

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sync from URL (back/forward navigation)
  useEffect(() => {
    const currentId = currentContainer?.id ?? null;
    if (urlContainerId !== undefined && urlContainerId !== currentId) {
      if (urlContainerId === null) {
        setCurrentContainer(null);
        setNavigationPath([]);
      } else {
        const restoreContainer = async () => {
          try {
            const container = await solutionContainerService.getContainer(urlContainerId);
            setCurrentContainer(container);
            const path: any[] = [];
            let cId: number | null = container.parent ?? null;
            while (cId) {
              const c = await solutionContainerService.getContainer(cId);
              path.unshift(c);
              cId = c.parent ?? null;
            }
            path.push(container);
            setNavigationPath(path);
          } catch (err) {
            console.error('Error restoring container from URL:', err);
          }
        };
        restoreContainer();
      }
    }
  }, [urlContainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refs to track current values for saving
  const currentSolutionRef = useRef<{
    solution: Solution | null;
    latex: string;
    python: string;
    tags: string[];
    skills: Skill[];
  }>({
    solution: null,
    latex: '',
    python: '',
    tags: [],
    skills: []
  });

  // Update ref whenever values change
  useEffect(() => {
    currentSolutionRef.current = {
      solution: selectedSolution,
      latex: latexTemplate,
      python: pythonFunction,
      tags,
      skills: selectedSkills
    };
  }, [selectedSolution, latexTemplate, pythonFunction, tags, selectedSkills]);

  const initialLoadDone = useRef(false);

  const loadItems = async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);

      if (currentContainer) {
        // Load contents of the current container
        const contents = await solutionContainerService.getContents(currentContainer.id);
        setContainers(Array.isArray(contents?.containers) ? contents.containers : []);
        setSolutions(Array.isArray(contents?.solutions) ? contents.solutions : []);
      } else {
        // Load root level items (containers and solutions without parent)
        const containersData = await solutionContainerService.getContainers(null);
        const solutionsData = await solutionService.getSolutions();

        setContainers(Array.isArray(containersData) ? containersData : []);
        setSolutions(Array.isArray(solutionsData) ? solutionsData : []);
      }
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Erreur lors du chargement des données');
      setContainers([]);
      setSolutions([]);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };

  const loadSkills = async () => {
    try {
      const data = await skillService.getAll({ max_depth: 1 });
      setAllSkills(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading skills:', error);
      setAllSkills([]);
    }
  };

  useEffect(() => {
    loadItems();
    loadSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContainer]);  // Reload when currentContainer changes

  // Register callback to save before tab change
  useEffect(() => {
    const saveBeforeTabChange = async () => {
      const current = currentSolutionRef.current;
      if (current.solution && (
          current.latex !== current.solution.latex_template ||
          current.python !== (current.solution.python_function || '') ||
          JSON.stringify(current.tags) !== JSON.stringify(current.solution.tags || []) ||
          JSON.stringify(current.skills.map(s => s.id).sort()) !== JSON.stringify((current.solution.skills || []).map((s: Skill) => s.id).sort())
      )) {
        try {
          await solutionService.updateSolution(current.solution.id, {
            latex_template: current.latex,
            python_function: current.python,
            tags: current.tags,
            skill_ids: current.skills.map(s => s.id),
          });
          console.log('Solution saved before tab change');
        } catch (error) {
          console.error('Error saving solution before tab change:', error);
        }
      }
    };

    registerBeforeTabChange('solutions', saveBeforeTabChange);

    return () => {
      unregisterBeforeTabChange('solutions');
    };
  }, []); // Empty dependency array - only register/unregister on mount/unmount

  // Save on component unmount (for navigation outside Workshop)
  useEffect(() => {
    return () => {
      const current = currentSolutionRef.current;
      if (current.solution && (
          current.latex !== current.solution.latex_template ||
          current.python !== (current.solution.python_function || '') ||
          JSON.stringify(current.tags) !== JSON.stringify(current.solution.tags || []) ||
          JSON.stringify(current.skills.map(s => s.id).sort()) !== JSON.stringify((current.solution.skills || []).map((s: Skill) => s.id).sort())
      )) {
        // Fire off the save request without awaiting
        solutionService.updateSolution(current.solution.id, {
          latex_template: current.latex,
          python_function: current.python,
          tags: current.tags,
          skill_ids: current.skills.map(s => s.id),
        })
          .then(() => console.log('Solution saved on unmount'))
          .catch(err => console.error('Error saving solution on unmount:', err));
      }
    };
  }, []); // Empty dependency array - only run on unmount

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    setActiveItemType(id.startsWith('container-') ? 'container' : 'solution');
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveItemType('');

    if (!over || !active) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Handle drop into container
    const isActiveSolution = activeId.startsWith('solution-');
    const isOverContainer = overId.startsWith('container-');

    if (isActiveSolution && isOverContainer) {
      const solutionId = parseInt(activeId.replace('solution-', ''));
      const containerId = parseInt(overId.replace('container-', ''));
      const solution = solutions.find(s => s.id === solutionId);

      if (solution) {
        try {
          await solutionService.updateSolution(solutionId, {
            container: containerId
          });
          await loadItems();
          toast.success('Solution déplacée dans le contenant');
        } catch (error) {
          toast.error('Erreur lors du déplacement');
        }
      }
      return;
    }

    // Handle regular reordering
    if (active.id !== over.id) {
      const allItems = [...containers, ...solutions];
      const activeIndex = allItems.findIndex(item =>
        `${('solutions_count' in item ? 'container' : 'solution')}-${item.id}` === active.id
      );
      const overIndex = allItems.findIndex(item =>
        `${('solutions_count' in item ? 'container' : 'solution')}-${item.id}` === over.id
      );

      if (activeIndex !== -1 && overIndex !== -1) {
        const newItems = arrayMove(allItems, activeIndex, overIndex);

        // Separate containers and solutions
        const newContainers = newItems.filter(item => 'solutions_count' in item) as SolutionContainer[];
        const newSolutions = newItems.filter(item => 'pattern_title' in item) as Solution[];

        setContainers(newContainers);
        setSolutions(newSolutions);

        // Update order values
        const containerOrder = newContainers.map((item, index) => ({
          id: item.id,
          order: index,
          parent: currentContainer?.id || null
        }));
        const solutionOrder = newSolutions.map((item, index) => ({
          id: item.id,
          order: index + newContainers.length,
          container: currentContainer?.id || null
        }));

        // Save new order to backend
        if (containerOrder.length > 0) {
          solutionContainerService.reorderContainers(containerOrder).catch(error => {
            console.error('Error reordering containers:', error);
            loadItems();
          });
        }
        if (solutionOrder.length > 0) {
          solutionService.reorderSolutions(solutionOrder).catch(error => {
            console.error('Error reordering solutions:', error);
            loadItems();
          });
        }
      }
    }
  };

  // Navigation handlers
  const handleItemSelect = (item: Solution | SolutionContainer, isContainer: boolean) => {
    if (isContainer) {
      const container = item as SolutionContainer;
      setNavigationPath([...navigationPath, container]);
      setCurrentContainer(container);
      onNavChange?.(container.id, null, container.title);
      setSelectedSolution(null);
      setLatexTemplate('');
      setPythonFunction('');
      setTags([]);
      setSelectedSkills([]);
    } else {
      handleSolutionSelect(item as Solution);
    }
  };

  const handleNavigateBack = () => {
    if (navigationPath.length > 0) {
      const newPath = [...navigationPath];
      newPath.pop();
      setNavigationPath(newPath);
      const newContainer = newPath[newPath.length - 1] || null;
      setCurrentContainer(newContainer);
      onNavChange?.(newContainer?.id ?? null, null, newContainer?.title);
    }
  };

  const handleNavigateTo = (index: number) => {
    const newPath = navigationPath.slice(0, index + 1);
    setNavigationPath(newPath);
    const newContainer = newPath[newPath.length - 1] || null;
    setCurrentContainer(newContainer);
    onNavChange?.(newContainer?.id ?? null, null, newContainer?.title);
  };

  const handleSolutionSelect = async (solution: Solution) => {
    // Save current solution before switching
    if (selectedSolution) {
      try {
        await solutionService.updateSolution(selectedSolution.id, {
          latex_template: latexTemplate,
          python_function: pythonFunction,
          tags,
          skill_ids: selectedSkills.map(s => s.id),
        });
        console.log('Saved solution before switching');
      } catch (error) {
        console.error('Error saving solution:', error);
      }
    }

    // Reload the solution from server to get latest data
    try {
      const freshSolution = await solutionService.getSolution(solution.id);
      if (freshSolution) {
        setSelectedSolution(freshSolution);
        setLatexTemplate(freshSolution.latex_template);
        setPythonFunction(freshSolution.python_function || '');
        setTags(freshSolution.tags || []);
        setSelectedSkills(freshSolution.skills || []);

        // Charger les informations du pattern pour savoir s'il est réutilisable
        if (freshSolution.pattern) {
          try {
            const pattern = await patternService.getPattern(freshSolution.pattern);
            setIsPatternReusable(pattern.is_reusable);
          } catch (error) {
            console.error('Error loading pattern:', error);
            setIsPatternReusable(true);
          }
        } else {
          setIsPatternReusable(true);
        }
      } else {
        // Fallback to cached data if fetch fails
        setSelectedSolution(solution);
        setLatexTemplate(solution.latex_template);
        setPythonFunction(solution.python_function || '');
        setTags(solution.tags || []);
        setSelectedSkills(solution.skills || []);

        if (solution.pattern) {
          try {
            const pattern = await patternService.getPattern(solution.pattern);
            setIsPatternReusable(pattern.is_reusable);
          } catch (error) {
            console.error('Error loading pattern:', error);
            setIsPatternReusable(true);
          }
        } else {
          setIsPatternReusable(true);
        }
      }
    } catch (error) {
      console.error('Error fetching fresh solution data:', error);
      // Fallback to cached data
      setSelectedSolution(solution);
      setLatexTemplate(solution.latex_template);
      setPythonFunction(solution.python_function || '');
      setTags(solution.tags || []);
      setSelectedSkills(solution.skills || []);

      if (solution.pattern) {
        try {
          const pattern = await patternService.getPattern(solution.pattern);
          setIsPatternReusable(pattern.is_reusable);
        } catch (error) {
          console.error('Error loading pattern:', error);
          setIsPatternReusable(true);
        }
      } else {
        setIsPatternReusable(true);
      }
    }
  };

  const handlePatternSelect = (pattern: ExercisePattern) => {
    setSelectedPattern(pattern);
    setPatternDialogOpen(false);
  };

  const handleCreateSolution = async () => {
    if (!selectedPattern) {
      toast.error('Veuillez sélectionner un pattern');
      return;
    }

    try {
      if (!initialLoadDone.current) setLoading(true);
      const newSolution = await solutionService.createSolution({
        pattern: selectedPattern.id,
        latex_template: '',
        python_function: '',
        tags: [],
        container: currentContainer?.id || null,
      });
      toast.success('Solution créée avec succès');
      setCreateDialogOpen(false);
      setSelectedPattern(null);
      await loadItems();
      handleSolutionSelect(newSolution);
    } catch (error) {
      toast.error('Erreur lors de la création de la solution');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContainer = async () => {
    if (!newContainerName.trim()) {
      toast.error('Veuillez entrer un nom pour le container');
      return;
    }

    try {
      if (!initialLoadDone.current) setLoading(true);
      await solutionContainerService.createContainer({
        title: newContainerName,
        parent: currentContainer?.id || null,
        order: 0,
      });
      toast.success('Container créé avec succès');
      setCreateDialogOpen(false);
      setNewContainerName('');
      await loadItems();
    } catch (error) {
      toast.error('Erreur lors de la création du container');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    if (createDialogTab === 0) {
      handleCreateContainer();
    } else {
      handleCreateSolution();
    }
  };

  const handleDeleteSolution = async () => {
    if (!selectedSolution) return;

    const confirmDelete = window.confirm(
      `Êtes-vous sûr de vouloir supprimer cette solution pour "${selectedSolution.pattern_title}" ?`
    );

    if (!confirmDelete) return;

    try {
      if (!initialLoadDone.current) setLoading(true);
      await solutionService.deleteSolution(selectedSolution.id);
      toast.success('Solution supprimée avec succès');
      setSelectedSolution(null);
      setLatexTemplate('');
      setPythonFunction('');
      setTags([]);
      setSelectedSkills([]);
      await loadItems();
    } catch (error) {
      toast.error('Erreur lors de la suppression de la solution');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublicSolution = async (solutionId: number, newIsPublic: boolean) => {
    try {
      await solutionService.updateSolution(solutionId, { is_public: newIsPublic });
      toast.success(`Solution rendue ${newIsPublic ? 'publique' : 'privée'}`);

      // Update in the list
      setSolutions(solutions.map(s => s.id === solutionId ? { ...s, is_public: newIsPublic } : s));

      // Update selected solution if it's the same
      if (selectedSolution?.id === solutionId) {
        setSelectedSolution({ ...selectedSolution, is_public: newIsPublic });
      }
    } catch (error) {
      console.error('Error updating solution visibility:', error);
      toast.error('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleShowPattern = async () => {
    if (!selectedSolution || !selectedSolution.pattern) return;

    try {
      if (!initialLoadDone.current) setLoading(true);
      const pattern = await patternService.getPattern(selectedSolution.pattern);
      setPatternDetails(pattern);

      // Test generations if pattern is reusable
      if (pattern.is_reusable && pattern.python_generator) {
        await handleTestGenerations(pattern.python_generator);
      }

      setPatternModalOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement du pattern');
    } finally {
      setLoading(false);
    }
  };

  const handleTestGenerations = async (pythonGenerator: string) => {
    if (!selectedSolution?.pattern) {
      toast.error('Aucun pattern associé à cette solution');
      return;
    }

    try {
      setGenerationLoading(true);
      const response = await patternService.testGenerator(selectedSolution.pattern, pythonGenerator);
      if (response.success && response.results) {
        setGenerationResults(response.results);
      } else {
        setGenerationResults(null);
      }
    } catch (error: any) {
      setGenerationResults(null);
    } finally {
      setGenerationLoading(false);
    }
  };

  const saveSolution = async () => {
    if (!selectedSolution) return;

    try {
      console.log('Saving solution with python_function:', pythonFunction.substring(0, 100) + '...');
      await solutionService.updateSolution(selectedSolution.id, {
        latex_template: latexTemplate,
        python_function: pythonFunction,
        tags,
        skill_ids: selectedSkills.map(s => s.id),
      });
      console.log('Solution saved successfully');
    } catch (error) {
      console.error('Error saving solution:', error);
      throw error; // Re-throw to handle in calling function
    }
  };

  const handleRegeneratePatternValues = async () => {
    if (!selectedSolution) return;

    try {
      if (!initialLoadDone.current) setLoading(true);

      // First, save any pending changes
      await saveSolution();

      // Reload the solution from database to get the saved python_function
      const updatedSolution = await solutionService.getSolution(selectedSolution.id);

      // Update local state with saved values
      setPythonFunction(updatedSolution.python_function || '');
      setLatexTemplate(updatedSolution.latex_template || '');

      // Then generate pattern values
      const response = await solutionService.generatePatternValues(selectedSolution.id);

      if (response.success && response.pattern_generated_values && response.generated_values) {
        // Update the selected solution with new generated values and saved code
        setSelectedSolution({
          ...updatedSolution,
          pattern_generated_values: response.pattern_generated_values,
          generated_values: response.generated_values
        });

        // Reload the solutions list to update it
        await loadItems();

        toast.success('Valeurs du pattern régénérées avec succès');
      } else {
        toast.error(response.error || 'Erreur lors de la génération des valeurs du pattern');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Erreur lors de la génération des valeurs du pattern');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateSolutionValues = async () => {
    if (!selectedSolution) return;

    alert('NOUVEAU CODE - Version modifiée par Claude');

    try {
      if (!initialLoadDone.current) setLoading(true);

      // First, save any pending changes (especially python_function)
      await saveSolution();

      // Reload the solution from database to get the saved python_function
      const updatedSolution = await solutionService.getSolution(selectedSolution.id);
      console.log('Reloaded solution with python_function:', updatedSolution.python_function?.substring(0, 100) + '...');

      // Update local state with saved values
      setPythonFunction(updatedSolution.python_function || '');
      setLatexTemplate(updatedSolution.latex_template || '');

      // Then generate solution values using the saved code
      const response = await solutionService.generateSolutionValues(selectedSolution.id);

      if (response.success && response.generated_values) {
        // Update the selected solution with new generated values and saved code
        setSelectedSolution({
          ...updatedSolution,
          generated_values: response.generated_values
        });

        // Reload the solutions list to update it
        await loadItems();

        toast.success('Valeurs de la solution régénérées avec succès');
      } else {
        toast.error(response.error || 'Erreur lors de la génération des valeurs de la solution');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Erreur lors de la génération des valeurs de la solution');
    } finally {
      setLoading(false);
    }
  };

  const handleVisualizeSolution = async () => {
    if (!selectedSolution) return;

    try {
      if (!initialLoadDone.current) setLoading(true);

      // Save any pending changes (latex and python)
      await saveSolution();

      // Reload the solution from database to get the saved python_function
      const updatedSolution = await solutionService.getSolution(selectedSolution.id);

      // Update local state with saved values
      setPythonFunction(updatedSolution.python_function || '');
      setLatexTemplate(updatedSolution.latex_template || '');

      // Generate new values if pattern is reusable
      if (isPatternReusable) {
        const response = await solutionService.generateSolutionValues(selectedSolution.id);

        if (response.success && response.generated_values) {
          // Update the selected solution with new generated values and saved code
          setSelectedSolution({
            ...updatedSolution,
            generated_values: response.generated_values
          });
        } else {
          toast.error(response.error || 'Erreur lors de la génération des valeurs');
          setLoading(false);
          return;
        }
      } else {
        // If pattern is not reusable, just update with the reloaded solution
        setSelectedSolution(updatedSolution);
      }

      // Reload PDF
      setSolutionPdfKey(prev => prev + 1);

    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Erreur lors de la visualisation');
    } finally {
      setLoading(false);
    }
  };

  // Prepare sortable items
  const allItems = [...containers, ...solutions];
  const sortableItems = allItems.map(item =>
    `${('solutions_count' in item ? 'container' : 'solution')}-${item.id}`
  );

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Solutions List */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            {/* Navigation Breadcrumbs */}
            {navigationPath.length > 0 && (
              <Box mb={2}>
                <Breadcrumbs>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setNavigationPath([]);
                      setCurrentContainer(null);
                      onNavChange?.(null, null, undefined);
                    }}
                    underline="hover"
                    color="inherit"
                  >
                    Solutions
                  </Link>
                  {navigationPath.map((container, index) => (
                    <Link
                      key={container.id}
                      component="button"
                      variant="body2"
                      onClick={() => handleNavigateTo(index)}
                      underline="hover"
                      color={index === navigationPath.length - 1 ? "text.primary" : "inherit"}
                    >
                      {container.title}
                    </Link>
                  ))}
                </Breadcrumbs>
              </Box>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box display="flex" alignItems="center" gap={1}>
                {navigationPath.length > 0 && (
                  <IconButton size="small" onClick={handleNavigateBack}>
                    <ArrowBackIcon />
                  </IconButton>
                )}
                <Typography variant="h6">
                  {currentContainer ? currentContainer.title : 'Solutions'}
                </Typography>
              </Box>
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={() => setCreateDialogOpen(true)}
              >
                Nouveau
              </Button>
            </Box>

            {loading ? (
              <CircularProgress />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sortableItems}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={1}>
                    {allItems.map((item) => {
                      const isContainer = 'solutions_count' in item;
                      return (
                        <SortableItem
                          key={`${isContainer ? 'container' : 'solution'}-${item.id}`}
                          item={item}
                          isContainer={isContainer}
                          isSelected={!isContainer && selectedSolution?.id === item.id}
                          onSelect={handleItemSelect}
                          onTogglePublic={(solution, isPublic) => handleTogglePublicSolution(solution.id, isPublic)}
                          activeItemType={activeItemType}
                        />
                      );
                    })}
                  </Stack>
                </SortableContext>
              </DndContext>
            )}
          </Paper>
        </Grid>

        {/* Right Panel - Editor */}
        <Grid item xs={12} md={9}>
          <Paper sx={{
            p: 3,
            backgroundColor: selectedSolution?.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
            borderRadius: 2
          }}>
            {!selectedSolution ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="400px"
                color="text.secondary"
              >
                <Typography variant="h6" gutterBottom>
                  Aucune solution sélectionnée
                </Typography>
                <Typography variant="body2">
                  Sélectionnez une solution dans la liste ou créez-en une nouvelle
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {/* Pattern info */}
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                      Solution pour : {selectedSolution.pattern_title}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        startIcon={<PreviewIcon />}
                        onClick={handleShowPattern}
                        size="small"
                      >
                        Afficher le pattern
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteSolution}
                        size="small"
                      >
                        Supprimer
                      </Button>
                    </Box>
                  </Box>
                </Grid>

                {/* Savoir-faire (toute la largeur) */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Savoir-faire de la solution
                  </Typography>
                  <Autocomplete
                    multiple
                    options={allSkills}
                    getOptionLabel={(option) => option.name}
                    value={selectedSkills}
                    onChange={(_, newValue) => setSelectedSkills(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Sélectionnez les savoir-faire..."
                        variant="outlined"
                        size="small"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const hasChildren = option.children_skills && option.children_skills.length > 0;
                        return (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            size="small"
                            color={hasChildren ? 'warning' : 'default'}
                            variant={hasChildren ? 'outlined' : 'filled'}
                          />
                        );
                      })
                    }
                  />
                  {/* Skill refinement suggestions */}
                  {selectedSkills.some(s => s.children_skills && s.children_skills.length > 0) && (
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200', borderRadius: 1, backgroundColor: 'rgba(255, 167, 38, 0.08)' }}>
                      <Typography variant="caption" color="warning.dark" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                        Suggestion de précision
                      </Typography>
                      {selectedSkills.filter(s => s.children_skills && s.children_skills.length > 0).map(skill => (
                        <Box key={skill.id} sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            « {skill.name} » peut être précisé :
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {skill.children_skills!.slice(0, 10).map(child => {
                              const alreadySelected = selectedSkills.some(s => s.id === child.id);
                              return (
                                <Chip
                                  key={child.id}
                                  label={child.name}
                                  size="small"
                                  variant="outlined"
                                  color={child.is_reference ? 'secondary' : 'primary'}
                                  disabled={alreadySelected}
                                  onClick={() => {
                                    if (!alreadySelected) {
                                      const childSkill = allSkills.find(s => s.id === child.id);
                                      if (childSkill) {
                                        setSelectedSkills(prev => [...prev, childSkill]);
                                      }
                                    }
                                  }}
                                  sx={{ cursor: alreadySelected ? 'default' : 'pointer', fontSize: '0.7rem' }}
                                />
                              );
                            })}
                            {skill.children_skills!.length > 10 && (
                              <Chip label={`+${skill.children_skills!.length - 10}`} size="small" />
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Grid>

                {/* Template LaTeX à gauche */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Template LaTeX
                  </Typography>
                  <LatexEditor
                    value={latexTemplate}
                    onChange={setLatexTemplate}
                    placeholder="Entrez le template LaTeX de la solution..."
                    helperText="Utilisez {{variable}} pour les valeurs dynamiques du pattern"
                    height={600}
                  />
                </Grid>

                {/* Aperçu PDF à droite */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Aperçu PDF
                  </Typography>
                  <Box display="flex" justifyContent="flex-end" mb={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleVisualizeSolution}
                      disabled={loading}
                    >
                      {loading ? 'Génération...' : 'Visualiser la solution'}
                    </Button>
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, height: 'calc(600px - 36px)', overflow: 'auto' }}>
                    <PdfViewer
                      key={solutionPdfKey}
                      url={`/solutions/${selectedSolution.id}/compile_pdf/?t=${solutionPdfKey}`}
                      title={`solution_${selectedSolution.id}`}
                    />
                  </Paper>
                </Grid>

                {/* Fonction Python en dessous */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Fonction Python (optionnel)
                  </Typography>
                  <PythonEditor
                    value={pythonFunction}
                    onChange={(value) => setPythonFunction(value)}
                    placeholder="Entrez une fonction Python optionnelle pour des calculs supplémentaires..."
                    helperText={
                      !isPatternReusable
                        ? "Ce champ est désactivé car le pattern est statique (non réutilisable)"
                        : "Cette fonction peut être utilisée pour des transformations ou calculs"
                    }
                    disabled={!isPatternReusable}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '400px',
                        alignItems: 'flex-start',
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '0.875rem',
                        height: '100% !important',
                        overflow: 'auto !important',
                        maxHeight: '100%',
                      },
                      '& textarea': {
                        height: '100% !important',
                        overflow: 'auto !important',
                      }
                    }}
                  />
                </Grid>

                {/* Valeurs générées par la solution */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">
                      Valeurs finales générées par la solution
                    </Typography>
                    {isPatternReusable && selectedSolution && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleRegenerateSolutionValues}
                        disabled={loading}
                      >
                        {loading ? 'Génération...' : 'Générer'}
                      </Button>
                    )}
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, height: '400px', overflow: 'auto', bgcolor: '#f5f5f5' }}>
                    {selectedSolution?.generated_values ? (
                      <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedSolution.generated_values, null, 2)}
                      </pre>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {isPatternReusable
                          ? 'Aucune valeur générée. Cliquez sur "Générer" pour générer des valeurs.'
                          : 'Le pattern est statique (non réutilisable), aucune valeur à générer.'}
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Valeurs générées par le pattern */}
                <Grid item xs={12} md={6}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">
                      Valeurs générées par le pattern
                    </Typography>
                    {isPatternReusable && selectedSolution && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleRegeneratePatternValues}
                        disabled={loading}
                      >
                        {loading ? 'Génération...' : 'Re-générer'}
                      </Button>
                    )}
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, height: '400px', overflow: 'auto', bgcolor: '#f5f5f5' }}>
                    {selectedSolution?.pattern_generated_values ? (
                      <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedSolution.pattern_generated_values, null, 2)}
                      </pre>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {isPatternReusable
                          ? 'Aucune valeur générée. Cliquez sur "Re-générer" pour générer des valeurs.'
                          : 'Le pattern est statique (non réutilisable), aucune valeur à générer.'}
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Tags à droite */}
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Tags</Typography>
                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                      {tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          onDelete={() => handleRemoveTag(tag)}
                          size="small"
                        />
                      ))}
                    </Box>
                    <Box display="flex" gap={1} mb={3}>
                      <TextField
                        size="small"
                        placeholder="Ajouter un tag"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                      />
                      <Button size="small" onClick={handleAddTag}>
                        Ajouter
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Create Solution/Container Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setCreateDialogTab(1);
          setSelectedPattern(null);
          setNewContainerName('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Tabs
            value={createDialogTab}
            onChange={(_, newValue) => setCreateDialogTab(newValue)}
            variant="fullWidth"
          >
            <Tab icon={<FolderIcon />} iconPosition="start" label="Container" />
            <Tab icon={<FileIcon />} iconPosition="start" label="Solution" />
          </Tabs>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '150px' }}>
          {createDialogTab === 0 ? (
            <TextField
              label="Nom du container"
              value={newContainerName}
              onChange={(e) => setNewContainerName(e.target.value)}
              fullWidth
              margin="dense"
              autoFocus
              sx={{ mt: 1 }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newContainerName.trim()) {
                  handleCreate();
                }
              }}
            />
          ) : (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Sélectionnez un pattern d'exercice pour créer une solution
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setPatternDialogOpen(true)}
                sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.5 }}
              >
                {selectedPattern ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <FileIcon fontSize="small" />
                    <Typography>
                      {selectedPattern.title} ({selectedPattern.exercise_count || 0} exercices)
                    </Typography>
                  </Box>
                ) : (
                  <Typography color="text.secondary">
                    Sélectionner un pattern...
                  </Typography>
                )}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setCreateDialogTab(1);
            setSelectedPattern(null);
            setNewContainerName('');
          }}>
            Annuler
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            color="primary"
            disabled={createDialogTab === 0 ? !newContainerName.trim() : !selectedPattern}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pattern Selection Dialog */}
      <PatternSelectionDialog
        open={patternDialogOpen}
        onClose={() => setPatternDialogOpen(false)}
        onSelect={handlePatternSelect}
      />

      {/* Pattern Display Modal */}
      <Dialog
        open={patternModalOpen}
        onClose={() => {
          setPatternModalOpen(false);
          setPatternDetails(null);
          setGenerationResults(null);
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Pattern : {patternDetails?.title}
        </DialogTitle>
        <DialogContent>
          {patternDetails && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Ligne 1 : PDF avec itérations (toute la largeur) */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Aperçu PDF avec itérations
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, height: '500px', overflow: 'auto' }}>
                  {patternDetails.is_reusable ? (
                    <PatternPdfViewer
                      latexTemplate={patternDetails.latex_template}
                      pythonGenerator={patternDetails.python_generator}
                      isReusable={patternDetails.is_reusable}
                      iterations={3}
                      patternId={patternDetails.id}
                      onPdfGenerated={() => {}}
                    />
                  ) : (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      height="100%"
                    >
                      <Typography variant="body2" color="text.secondary">
                        Ce pattern n'est pas réutilisable (pas d'itérations)
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Ligne 2 : Code Python + Exemples d'itérations */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Code Python
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '400px',
                    overflow: 'auto',
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {patternDetails.python_generator || 'Aucun code Python défini'}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Exemples d'itérations
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '400px',
                    overflow: 'auto',
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {generationLoading ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <CircularProgress size={24} />
                    </Box>
                  ) : !patternDetails.is_reusable ? (
                    <Typography variant="body2" color="text.secondary">
                      Ce pattern n'est pas réutilisable
                    </Typography>
                  ) : generationResults ? (
                    <Box>
                      {generationResults.map((result, index) => (
                        <Box key={index} mb={2}>
                          <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                            Itération {index + 1}:
                          </Typography>
                          <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(result, null, 2)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucune itération générée
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Ligne 3 : Code LaTeX + PDF */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Template LaTeX
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '400px',
                    overflow: 'auto',
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {patternDetails.latex_template}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Aperçu PDF du pattern
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, height: '400px', overflow: 'auto' }}>
                  {patternDetails.preview_pdf_url ? (
                    <iframe
                      src={patternDetails.preview_pdf_url}
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                      title="Aperçu du pattern"
                    />
                  ) : (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      height="100%"
                    >
                      <Typography variant="body2" color="text.secondary">
                        Aucun aperçu PDF disponible
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPatternModalOpen(false);
            setPatternDetails(null);
            setGenerationResults(null);
          }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Solutions;

